import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Play,
  Upload,
  X,
} from "lucide-react";
import { api } from "../api";
import { applyWorksheetStyle, styleWorkbook } from "../excel";
import {
  Button,
  EmptyState,
  FormField,
  Panel,
  StatusBadge,
  statusLabel,
} from "../components/ui";
import { PageHeader } from "../components/shell";
import { formatNumber, normalizeNumber } from "../numbers";
import type { BomListItem, ImportIssue } from "../types";

const fields = [
  ["productCode", "Mã sản phẩm"],
  ["productName", "Quy cách thành phẩm"],
  ["customerCode", "Mã khách hàng"],
  ["customerName", "Khách hàng"],
  ["materialCode", "Mã vật tư"],
  ["materialName", "Vật tư / bột"],
  ["unitCode", "Mã ĐVT"],
  ["unitName", "Tên ĐVT"],
  ["quantity", "Định mức sử dụng"],
  ["wastagePercent", "Hao hụt (%)"],
  ["accountCode", "Mã tài khoản kế toán"],
  ["finishedWeight", "Khối lượng thành phẩm"],
  ["usedWeight", "Khối lượng vật tư sử dụng"],
  ["note", "Ghi chú"],
] as const;
const defaultBomColumns = fields;
type FieldKey = (typeof fields)[number][0];
type Parsed = {
  headers: string[];
  rows: Record<string, unknown>[];
  sheetName: string;
};
type ImportRecord = {
  id: string;
  filename: string;
  status: string;
  summary: Record<string, number | string>;
  created_at: string;
  completed_at: string | null;
  bomIds: string[];
};
type ImportActionResponse = Partial<ImportRecord> & {
  ok?: boolean;
  job?: Partial<ImportRecord>;
};
type ImportProgress = Partial<
  Pick<
    ImportRecord,
    "id" | "filename" | "status" | "summary" | "created_at" | "completed_at" | "bomIds"
  >
>;

const IMPORT_CHUNK_SIZE = 5_000;

function importSummary(value: unknown): ImportRecord["summary"] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, entry]) => typeof entry === "number" || typeof entry === "string",
    ),
  );
}

function importBomIds(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((id): id is string => typeof id === "string")
    : [];
}

function mergeImportRecord(
  current: ImportRecord,
  response: ImportActionResponse | ImportProgress,
  fallbackStatus: string,
): ImportRecord {
  const payload = "job" in response && response.job ? response.job : response;
  return {
    id: typeof payload.id === "string" ? payload.id : current.id,
    filename:
      typeof payload.filename === "string" ? payload.filename : current.filename,
    status: typeof payload.status === "string" ? payload.status : fallbackStatus,
    summary:
      payload.summary === undefined ? current.summary : importSummary(payload.summary),
    created_at:
      typeof payload.created_at === "string"
        ? payload.created_at
        : current.created_at,
    completed_at:
      payload.completed_at === null || typeof payload.completed_at === "string"
        ? payload.completed_at
        : current.completed_at,
    bomIds: payload.bomIds === undefined ? current.bomIds : importBomIds(payload.bomIds),
  };
}

async function uploadImportChunks(importId: string, rows: Record<string, unknown>[]) {
  for (let start = 0, chunkNo = 0; start < rows.length; start += IMPORT_CHUNK_SIZE, chunkNo += 1) {
    await api(`/imports/${importId}/chunks`, {
        method: "POST",
        body: JSON.stringify({
          chunkNo,
          rows: rows.slice(start, start + IMPORT_CHUNK_SIZE),
        }),
      });
  }
}

function initialMapping(headers: string[]) {
  const clean = (value: string) => value.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("vi").replace(/đ/g, "d");
  const match = (patterns: string[]) =>
    headers.find((header) => patterns.some((pattern) => clean(header) === clean(pattern))) ?? headers.find((header) =>
      patterns.some((pattern) =>
        header.toLocaleLowerCase("vi").includes(pattern),
      ),
    ) ?? "";
  return {
    productCode: match(["mã sản phẩm", "mã sp"]),
    productName: match(["formed fish shape", "formed fish", "quy cách thành phẩm"]),
    customerCode: match(["mã khách hàng", "mã kh"]),
    customerName: match(["khách hàng"]),
    materialCode: match(["mã vật tư", "mã bột"]),
    materialName: match(["bột", "vật tư"]),
    unitCode: match(["mã đvt", "mã đơn vị"]),
    unitName: match(["tên đvt", "tên đơn vị", "đơn vị tính"]),
    quantity: match(["đm", "định mức"]),
    wastagePercent: match(["hao hụt (%)", "hao hụt", "tỷ lệ hao hụt"]),
    accountCode: match(["mã kế toán", "mã tài khoản"]),
    finishedWeight: match(["kl tp", "khối lượng thành phẩm"]),
    usedWeight: match(["kl bột", "kl vật tư", "khối lượng vật tư sử dụng"]),
    note: match(["ghi chú", "ghi chu"]),
  } satisfies Record<FieldKey, string>;
}

export function ImportsPage({
  embedded = false,
  onCompleted,
}: {
  embedded?: boolean;
  onCompleted?: (bomIds: string[]) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [mapping, setMapping] = useState<Record<FieldKey, string>>(
    initialMapping([]),
  );
  const [defaultUnit, setDefaultUnit] = useState("");
  const [importMode, setImportMode] = useState<"update_draft" | "new_version">("update_draft");
  const [issues, setIssues] = useState<ImportIssue[]>([]);
  const [previewed, setPreviewed] = useState(false);
  const [importing, setImporting] = useState(false);
  const [job, setJob] = useState<ImportRecord | null>(null);
  const [error, setError] = useState("");
  const workerRef = useRef<Worker | null>(null);
  const completedJobRef = useRef<string | null>(null);
  useEffect(() => () => workerRef.current?.terminate(), []);
  async function chooseFile(selected?: File) {
    if (!selected) return;
    if (selected.size > 50 * 1024 * 1024) {
      setError("Tệp XLSX vượt quá giới hạn 50 MB.");
      return;
    }
    setError("");
    setFile(selected);
    setParsed(null);
    setIssues([]);
    setPreviewed(false);
    setJob(null);
    const worker = new Worker(
      new URL("../workers/xlsx.worker.ts", import.meta.url),
      { type: "module" },
    );
    workerRef.current?.terminate();
    workerRef.current = worker;
    worker.onmessage = (
      event: MessageEvent<{
        kind: string;
        headers?: string[];
        rows?: Record<string, unknown>[];
        sheetName?: string;
        message?: string;
      }>,
    ) => {
      if (event.data.kind === "error") {
        setError(event.data.message ?? "Không thể đọc XLSX.");
        return;
      }
      const read = {
        headers: event.data.headers ?? [],
        rows: event.data.rows ?? [],
        sheetName: event.data.sheetName ?? "Data",
      };
      if (read.rows.length > 100_000) {
        setError("Tệp import vượt quá giới hạn 100.000 dòng.");
        return;
      }
      setParsed(read);
      setMapping(initialMapping(read.headers));
    };
    const buffer = await selected.arrayBuffer();
    worker.postMessage({ kind: "parse", buffer }, [buffer]);
  }
  function normalizedRows() {
    if (!parsed) return [];
    return parsed.rows.map((source) => {
      const row: Record<string, unknown> = { rowNo: source.rowNo };
      fields.forEach(([key]) => {
        row[key] = mapping[key] ? (source[mapping[key]] ?? "") : "";
      });
      for (const key of ["quantity", "wastagePercent", "finishedWeight", "usedWeight"] as const) {
        row[key] = normalizeNumber(row[key]) ?? row[key];
      }
      if (!row.unitCode && !row.unitName && defaultUnit.trim())
        row.unitName = defaultUnit.trim();
      return row;
    });
  }
  function validate(rows: Record<string, unknown>[]) {
    const localIssues: ImportIssue[] = [];
    const groups = new Map<string, Record<string, unknown>[]>();
    for (const row of rows) {
      const key = `${row.productCode || row.productName}|${row.customerCode || row.customerName}`;
      groups.set(key, [...(groups.get(key) ?? []), row]);
      const rowNo = Number(row.rowNo);
      if (!row.productCode && !row.productName)
        localIssues.push({
          rowNo,
          severity: "error",
          code: "MISSING_PRODUCT",
          message: "Thiếu sản phẩm",
          groupKey: key,
        });
      if (!row.customerCode && !row.customerName)
        localIssues.push({
          rowNo,
          severity: "error",
          code: "MISSING_CUSTOMER",
          message: "Thiếu khách hàng",
          groupKey: key,
        });
      if (!row.materialCode && !row.materialName)
        localIssues.push({
          rowNo,
          severity: "error",
          code: "MISSING_MATERIAL",
          message: "Thiếu vật tư",
          groupKey: key,
        });
      if (!row.unitCode && !row.unitName)
        localIssues.push({
          rowNo,
          severity: "error",
          code: "MISSING_UOM",
          message: "Thiếu đơn vị tính",
          groupKey: key,
        });
      if (!row.quantity || Number(row.quantity) < 0)
        localIssues.push({
          rowNo,
          severity: "warning",
          code: "MISSING_OR_INVALID_DM",
          message: "ĐM thiếu hoặc không hợp lệ",
          groupKey: key,
        });
      if (!row.accountCode || row.accountCode === "#N/A")
        localIssues.push({
          rowNo,
          severity: "warning",
          code: "MISSING_ACCOUNT_CODE",
          message: "Thiếu mã kế toán",
          groupKey: key,
        });
    }
    groups.forEach((group, key) => {
      if (group.length > 1000)
        localIssues.push({
          rowNo: Number(group[0]?.rowNo),
          severity: "error",
          code: "BOM_LINE_LIMIT",
          message: "Nhóm BOM vượt 1.000 dòng",
          groupKey: key,
        });
    });
    return localIssues;
  }
  function preview() {
    const localIssues = validate(normalizedRows());
    setIssues(localIssues);
    setPreviewed(true);
  }
  async function sendForValidation() {
    if (!file || !parsed) return;
    const rows = normalizedRows();
    const currentIssues = validate(rows);
    setIssues(currentIssues);
    setPreviewed(true);
    if (currentIssues.some((issue) => issue.severity === "error")) {
      setError("Cần xử lý các lỗi trước khi gửi kiểm tra.");
      return;
    }
    setImporting(true);
    setError("");
    try {
      const create = await api<{ id: string }>("/imports", {
        method: "POST",
        body: JSON.stringify({ filename: file.name, mapping: { ...mapping, _importMode: importMode } }),
      });
      const newJob: ImportRecord = {
        id: create.id,
        filename: file.name,
        status: "uploading",
        summary: {},
        created_at: new Date().toISOString(),
        completed_at: null,
        bomIds: [],
      };
      await Promise.all([
        api(`/imports/${create.id}/file`, {
          method: "PUT",
          headers: {
            "Content-Type":
              file.type ||
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          body: file,
        }),
        uploadImportChunks(create.id, rows),
      ]);
      const validation = await api<ImportActionResponse>(
        `/imports/${create.id}/validate`,
        { method: "POST" },
      );
      setJob(mergeImportRecord(newJob, validation, "validating"));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Không thể gửi tệp để kiểm tra.",
      );
    } finally {
      setImporting(false);
    }
  }
  async function saveValidatedImport() {
    if (!job || job.status !== "ready") return;
    setImporting(true);
    setError("");
    try {
      const commit = await api<ImportActionResponse>(`/imports/${job.id}/commit`, {
        method: "POST",
      });
      setJob(mergeImportRecord(job, commit, "processing"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể lưu dữ liệu.");
    } finally {
      setImporting(false);
    }
  }
  useEffect(() => {
    if (!job?.id || !["validating", "processing", "uploading"].includes(job.status)) return;
    let cancelled = false;
    let refreshing = false;
    const refresh = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const update = await api<ImportRecord>(`/imports/${job.id}`);
        if (!cancelled) setJob(update);
      } catch {
        /* next poll */
      } finally {
        refreshing = false;
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 1_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [job?.id, job?.status]);
  useEffect(() => {
    if (job?.status !== "completed" || completedJobRef.current === job.id) return;
    completedJobRef.current = job.id;
    onCompleted?.(job.bomIds);
  }, [job?.id, job?.status, job?.bomIds, onCompleted]);
  return (
    <div className={embedded ? "transfer-embedded" : "page"}>
      {embedded ? (
        <div className="transfer-actions">
          <DownloadTemplateButton />
          <ExportButton />
        </div>
      ) : (
        <PageHeader
          title="Nhập / xuất dữ liệu"
          actions={<><DownloadTemplateButton /><ExportButton /></>}
        />
      )}
      <div className="import-layout">
        <Panel
          title="Nhập định mức vật tư từ tệp Excel"
          actions={
            file ? (
              <Button
                variant="outline"
                onClick={() => {
                  setFile(null);
                  setParsed(null);
                  setIssues([]);
                  setPreviewed(false);
                  setJob(null);
                }}
              >
                {" "}
                <X size={16} /> Tệp khác
              </Button>
            ) : undefined
          }
        >
          <p className="catalog-guidance"><strong>Nhập trọn bộ BOM</strong><span>Tệp mẫu có sẵn các cột sản phẩm, khách hàng, vật tư, đơn vị tính và định mức. Danh mục chưa có sẽ được nhận diện từ mã/tên trong tệp để tạo tạm khi lưu.</span></p>
          {!file ? (
            <label className="drop-zone">
              <FileSpreadsheet size={38} />
              <strong>Chọn tệp Excel để nhập</strong>
              <span>
                Giới hạn 50 MB, tối đa 100.000 dòng. Tệp gốc được lưu an toàn.
              </span>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => chooseFile(event.target.files?.[0])}
              />
            </label>
          ) : (
            <div className="import-wizard">
              <div className="import-steps">
                <span className="complete">1. Chọn tệp</span>
                <span className={parsed ? "complete" : ""}>2. Ghép cột</span>
                <span className={previewed || job ? "complete" : ""}>
                  3. Kiểm tra
                </span>
                <span className={job ? "complete" : ""}>4. Kết quả</span>
              </div>
              {parsed && (
                <>
                  <div className="file-summary">
                    <FileSpreadsheet size={23} />
                    <div>
                      <strong>{file.name}</strong>
                      <span>
                        Trang tính {parsed.sheetName} ·{" "}
                        {formatNumber(parsed.rows.length)} dòng
                      </span>
                    </div>
                  </div>
                  <h3>Ghép cột dữ liệu</h3>
                  <div className="mapping-grid">
                    {fields.map(([key, label]) => (
                      <FormField key={key} label={label}>
                        <select
                          value={mapping[key]}
                          onChange={(event) => {
                            setPreviewed(false);
                            setMapping((current) => ({
                              ...current,
                              [key]: event.target.value,
                            }));
                          }}
                        >
                          <option value="">Không sử dụng</option>
                          {parsed.headers.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </FormField>
                    ))}
                  </div>
                  <div className="form-grid">
                    <FormField label="Cách cập nhật BOM">
                      <select value={importMode} onChange={(event) => { setPreviewed(false); setImportMode(event.target.value as "update_draft" | "new_version"); }}>
                        <option value="update_draft">Cập nhật BOM nháp đang có</option>
                        <option value="new_version">Tạo phiên bản BOM nháp mới</option>
                      </select>
                      <small>Cập nhật chỉ thay đổi BOM ở trạng thái đang soạn; BOM đã duyệt luôn được giữ nguyên.</small>
                    </FormField>
                    <FormField label="Đơn vị mặc định (nếu tệp không có cột ĐVT)">
                      <input
                        value={defaultUnit}
                        onChange={(event) => {
                          setPreviewed(false);
                          setDefaultUnit(event.target.value);
                        }}
                        placeholder="Ví dụ: kg"
                      />
                    </FormField>
                  </div>
                  <div className="wizard-actions">
                    <Button variant="outline" onClick={preview}>
                      <CheckCircle2 size={16} /> Kiểm tra dữ liệu
                    </Button>
                    {job?.status === "ready" ? (
                      <Button disabled={importing} onClick={saveValidatedImport}>
                        <Upload size={16} /> Lưu kết quả
                      </Button>
                    ) : (
                      <Button
                        disabled={importing || Boolean(job) || !parsed.rows.length || !previewed}
                        onClick={sendForValidation}
                      >
                        <Upload size={16} /> Gửi kiểm tra
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </Panel>
        <ImportSummary
          parsed={parsed}
          issues={issues}
          job={job}
          error={error}
        />
      </div>
    </div>
  );
}

function ImportSummary({
  parsed,
  issues,
  job,
  error,
}: {
  parsed: Parsed | null;
  issues: ImportIssue[];
  job: ImportRecord | null;
  error: string;
}) {
  const warnings = issues.filter((item) => item.severity === "warning");
  const errors = issues.filter((item) => item.severity === "error");
  const failureReason =
    job?.status === "failed" && typeof job.summary.error === "string"
      ? job.summary.error
      : "";
  return (
    <Panel className="import-summary" title="Tiến trình nhập dữ liệu">
      {error && <p className="form-error">{error}</p>}
      {!parsed ? (
        <EmptyState
          title="Chờ tệp XLSX"
          detail="Sau khi chọn tệp, trạng thái kiểm tra và kết quả sẽ hiển thị ở đây."
        />
      ) : (
        <>
          <div className="summary-cards">
            <div>
              <span>Tổng số dòng</span>
              <strong>{formatNumber(parsed.rows.length)}</strong>
            </div>
            <div className="valid">
              <span>Cảnh báo</span>
              <strong>{formatNumber(warnings.length)}</strong>
            </div>
            <div className="warning">
              <span>Lỗi chặn</span>
              <strong>{formatNumber(errors.length)}</strong>
            </div>
          </div>
          {job && (
            <div className="job-status">
              <StatusBadge status={job.status} />
              <strong>{job.filename}</strong>
              <p>
                {job.status === "completed"
                  ? `Hoàn tất: ${job.summary.importedGroups ?? 0}/${job.summary.totalGroups ?? 0} nhóm BOM.`
                  : job.status === "ready" ? "Dữ liệu đã được kiểm tra. Chọn “Lưu kết quả” để hoàn tất."
                    : job.status === "failed"
                      ? failureReason || "Không thể hoàn tất lần nhập này. Vui lòng kiểm tra lại dữ liệu và thử lại."
                      : "Hệ thống đang kiểm tra và xử lý dữ liệu theo từng nhóm BOM."}
              </p>
            </div>
          )}
          {issues.length ? (
            <>
              <h3>Danh sách vấn đề ({issues.length})</h3>
              <div className="issues">
                <table>
                  <thead>
                    <tr>
                      <th>Dòng</th>
                      <th>Mức độ</th>
                      <th>Diễn giải</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.slice(0, 8).map((issue, index) => (
                      <tr key={`${issue.rowNo}-${index}`}>
                        <td>{issue.rowNo}</td>
                        <td>
                          <span
                            className={
                              issue.severity === "error"
                                ? "issue-error"
                                : "issue-warning"
                            }
                          >
                            {issue.severity === "error" ? "Lỗi" : "Cảnh báo"}
                          </span>
                        </td>
                        <td>{issue.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="muted">
              <AlertTriangle size={15} /> Hãy kiểm tra dữ liệu trước khi lưu.
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

function ExportButton() {
  const [loading, setLoading] = useState(false);
  async function exportXlsx() {
    setLoading(true);
    try {
      const response = await api<{ items: BomListItem[] }>("/exports/boms");
      const XLSX = await import("xlsx-js-style");
      const rows = response.items.map((bom) => ({
        "Mã BOM": bom.code,
        "Sản phẩm": bom.product.name,
        "Khách hàng": bom.customer.name,
        "Phiên bản": bom.versionNo,
        "Trạng thái": statusLabel(bom.status),
      }));
      const sheet = XLSX.utils.json_to_sheet(rows);
      applyWorksheetStyle(XLSX, sheet, { headerRows: [0], dataStartsAt: 1, numberColumns: [3], columnWidths: [22, 34, 34, 12, 16] });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "BOM");
      styleWorkbook(XLSX, workbook, "Danh sách BOM");
      XLSX.writeFile(workbook, "bom-system-export.xlsx");
    } finally {
      setLoading(false);
    }
  }
  return (
    <Button variant="outline" disabled={loading} onClick={exportXlsx}>
      <Download size={16} /> Xuất tệp Excel
    </Button>
  );
}

function DownloadTemplateButton() {
  const [loading, setLoading] = useState(false);
  async function downloadTemplate() {
    setLoading(true);
    try {
      const XLSX = await import("xlsx-js-style");
      const headers = defaultBomColumns.map(([, label]) => label);
      const example = [
        "SP001", "Cá tra tẩm bột", "KH001", "Khách hàng mẫu", "VT001", "Bột mẫu", "KG", "Kilogam", 1.25, 0, "621", 1000, 1250, "Thay bằng số liệu thực tế",
      ];
      const sheet = XLSX.utils.aoa_to_sheet([headers, example]);
      applyWorksheetStyle(XLSX, sheet, { headerRows: [0], dataStartsAt: 1, numberColumns: [8, 9, 11, 12], columnWidths: defaultBomColumns.map(([, label]) => Math.max(16, label.length + 3)) });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "BOM");
      const guide = XLSX.utils.aoa_to_sheet([
        ["HƯỚNG DẪN NHẬP BOM"],
        ["1", "Điền một dòng cho mỗi vật tư trong BOM. Các dòng có cùng sản phẩm và khách hàng sẽ thành một BOM."],
        ["2", "Mã và tên sản phẩm, khách hàng, vật tư, đơn vị tính: nếu chưa có, hệ thống tự tạo danh mục tạm để hoàn thiện sau."],
        ["3", "Định mức sử dụng là bắt buộc. Nhập số theo dạng 1.250,5 hoặc 1,250.5 đều được."],
        ["4", "Mã kế toán, khối lượng thành phẩm, khối lượng vật tư sử dụng và ghi chú là thông tin theo từng dòng vật tư."],
        ["5", "Không sửa tên cột của trang BOM. Có thể xóa dòng ví dụ trước khi nhập dữ liệu thật."],
      ]);
      guide["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
      applyWorksheetStyle(XLSX, guide, { titleRows: [0], dataStartsAt: 1, columnWidths: [8, 120] });
      XLSX.utils.book_append_sheet(workbook, guide, "Hướng dẫn");
      styleWorkbook(XLSX, workbook, "Mẫu nhập BOM");
      XLSX.writeFile(workbook, "mau-nhap-bom.xlsx");
    } finally {
      setLoading(false);
    }
  }
  return (
    <Button variant="outline" disabled={loading} onClick={downloadTemplate}>
      <Download size={16} /> Tải tệp mẫu Excel
    </Button>
  );
}
